import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { IoMdArrowRoundBack, IoMdEye } from 'react-icons/io';
import PageForm from '../../templates/PageForm';
import ATable, { ATableHeader } from '../../organisms/ATable';
import { useSignal } from '@preact/signals-react';
import { recommendationBFFService } from '../../../services/RecommendationBFFService';

interface DocumentRecommendation {
    document: string;
    entityName: string;
    entityType: 'CPF' | 'CNPJ';
    recommendation: 'APPROVED' | 'REJECTED' | 'PENDING';
    creditFlag: boolean;
    triggeredRules: string[];
    provider: string;
    rawData: any;
}

const RecommendationEngineRecommendationView: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    
    const [batchData, setBatchData] = useState<any>(null);
    const [batchDataRef, setBatchDataRef] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const headers = useSignal<ATableHeader[]>([]);
    const rows = useSignal<any[]>([]);

    useEffect(() => {
        if (id) {
            loadBatchDetails();
        }
    }, [id]);

    const loadBatchDetails = async () => {
        try {
            setLoading(true);
            
            // Usar novo serviço BFF que consolida data-provider + recommendation-engine
            console.log('🔄 Carregando análise BFF do lote:', id);
            const enrichedBatchData = await recommendationBFFService.analyzeBatch(id!);
            
            setBatchData(enrichedBatchData);
            setBatchDataRef(enrichedBatchData);
            
            generateDocumentRecommendations(enrichedBatchData);
            
        } catch (error) {
            console.error('❌ Erro ao carregar detalhes do lote:', error);
            setError(error instanceof Error ? error.message : 'Erro desconhecido');
        } finally {
            setLoading(false);
        }
    };

    const generateDocumentRecommendations = (batchData: any) => {
        try {
            if (!batchData || !batchData.reports || !Array.isArray(batchData.reports)) {
                console.warn('Dados do lote inválidos ou sem reports');
                return;
            }

            // Agrupar reports por documento para unificar CNPJs duplicados
            const documentMap = new Map<string, DocumentRecommendation>();
            
            batchData.reports.forEach((report: any) => {
                const document = report.document || report.social_id || 'N/A';
                const entityType = report.backend_document_type || 
                                   (report.document && report.document.replace(/\D/g, '').length === 11 ? 'CPF' : 'CNPJ');
                
                if (!documentMap.has(document)) {
                    // Primeira ocorrência - criar entrada
                    documentMap.set(document, {
                        document: document,
                        entityName: report.entity_name || report.name || 'Nome não disponível',
                        entityType: entityType,
                        recommendation: mapRecommendation(report.backend_recommendation),
                        creditFlag: report.backend_credit_flag || false,
                        triggeredRules: report.backend_reasons || [],
                        provider: batchData.provider || 'UNKNOWN',
                        rawData: report
                    });
                } else {
                    // Documento duplicado - consolidar dados
                    const existing = documentMap.get(document)!;
                    
                    // Consolidar regras (sem duplicatas)
                    if (report.backend_reasons && Array.isArray(report.backend_reasons)) {
                        const existingRules = new Set(existing.triggeredRules);
                        report.backend_reasons.forEach((rule: string) => existingRules.add(rule));
                        existing.triggeredRules = Array.from(existingRules);
                    }
                    
                    // Prioridade de recomendação: REJECTED > PENDING > APPROVED
                    const newRecommendation = mapRecommendation(report.backend_recommendation);
                    if (newRecommendation === 'REJECTED') {
                        existing.recommendation = 'REJECTED';
                    } else if (newRecommendation === 'PENDING' && existing.recommendation !== 'REJECTED') {
                        existing.recommendation = 'PENDING';
                    }
                    
                    // Atualizar credit flag
                    if (report.backend_credit_flag) {
                        existing.creditFlag = true;
                    }
                }
            });

            const recommendations = Array.from(documentMap.values());

            // Configurar headers da tabela
            headers.value = [
                { id: 'document', title: 'Documento', canSort: true },
                { id: 'entityName', title: 'Nome/Razão Social', canSort: true },
                { id: 'entityType', title: 'Tipo', canSort: true },
                { id: 'recommendation', title: 'Recomendação', canSort: true },
                { id: 'triggeredRules', title: 'Regras Ativadas', canSort: false },
                { id: 'actions', title: 'Ações', canSort: false }
            ];

            // Configurar rows da tabela
            rows.value = recommendations.map((rec, index) => ({
                id: index,
                document: rec.document,
                entityName: rec.entityName,
                entityType: rec.entityType,
                recommendation: (
                    <span className={`px-2 py-1 rounded text-sm font-medium ${getRecommendationStyle(rec.recommendation)}`}>
                        {getRecommendationLabel(rec.recommendation)}
                    </span>
                ),
                triggeredRules: (
                    <div className="max-w-xs">
                        {rec.triggeredRules.length > 0 ? (
                            <ul className="text-sm text-gray-600">
                                {rec.triggeredRules.slice(0, 3).map((rule, idx) => (
                                    <li key={idx} className="truncate">• {rule}</li>
                                ))}
                                {rec.triggeredRules.length > 3 && (
                                    <li className="text-gray-400">... +{rec.triggeredRules.length - 3} mais</li>
                                )}
                            </ul>
                        ) : (
                            <span className="text-gray-400 text-sm">Nenhuma regra ativada</span>
                        )}
                    </div>
                ),
                actions: (
                    <button
                        onClick={() => viewDocumentDetails(rec)}
                        className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        title="Ver detalhes"
                    >
                        <IoMdEye size={16} />
                        Detalhes
                    </button>
                )
            }));

            console.log(`✅ Geradas ${recommendations.length} recomendações de documentos (unificadas)`);
            
        } catch (error) {
            console.error('❌ Erro ao gerar recomendações de documentos:', error);
        }
    };

    const mapRecommendation = (backendRecommendation: string): 'APPROVED' | 'REJECTED' | 'PENDING' => {
        switch (backendRecommendation) {
            case 'APROVADO':
                return 'APPROVED';
            case 'REPROVADO':
                return 'REJECTED';
            case 'PENDENTE':
                return 'PENDING';
            default:
                return 'PENDING';
        }
    };

    const getRecommendationStyle = (recommendation: string): string => {
        switch (recommendation) {
            case 'APPROVED':
                return 'bg-green-100 text-green-800';
            case 'REJECTED':
                return 'bg-red-100 text-red-800';
            case 'PENDING':
                return 'bg-yellow-100 text-yellow-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getRecommendationLabel = (recommendation: string): string => {
        switch (recommendation) {
            case 'APPROVED':
                return 'Aprovado';
            case 'REJECTED':
                return 'Reprovado';
            case 'PENDING':
                return 'Pendente';
            default:
                return 'Desconhecido';
        }
    };

    const viewDocumentDetails = (recommendation: DocumentRecommendation) => {
        console.log('📋 Visualizando detalhes do documento:', recommendation.document);
        navigate(`/recommendation-engine/document-details/${id}`, {
            state: { 
                document: recommendation.document,
                recommendation,
                batchData 
            }
        });
    };

    const handleBack = () => {
        navigate('/recommendation-engine/recommendations/0/20/createdAt/DESC');
    };

    if (loading) {
        return (
            <PageForm 
                pageTitle="Carregando Recomendações..."
                apiVersion="1.0"
                isLoading={false}
                breadcrumb={[]}
                controlSlot={<></>}
            >
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-gray-600">Processando análise BFF...</span>
                </div>
            </PageForm>
        );
    }

    if (error) {
        return (
            <PageForm 
                pageTitle="Erro ao Carregar Recomendações"
                apiVersion="1.0"
                isLoading={false}
                breadcrumb={[]}
                controlSlot={<></>}
            >
                <div className="text-center py-8">
                    <div className="text-red-600 mb-4">❌ {error}</div>
                    <button
                        onClick={() => window.location.reload()}
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                    >
                        Tentar Novamente
                    </button>
                </div>
            </PageForm>
        );
    }

    return (
        <PageForm 
            pageTitle={`Recomendações PLD - Lote ${id}`}
            apiVersion="1.0"
            isLoading={false}
            breadcrumb={[
                { label: 'Recommendation Engine', url: '/recommendation-engine' },
                { label: 'Recomendações', url: '#' }
            ]}
            controlSlot={<></>}
        >
            <div className="mb-6">
                <button
                    onClick={handleBack}
                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center gap-2"
                >
                    <IoMdArrowRoundBack />
                    Voltar para Lista de Lotes
                </button>
                
                {batchData && (
                    <div className="bg-gray-50 p-4 rounded-lg mb-6 mt-4">
                        <h3 className="font-semibold mb-2">Informações do Lote</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                                <span className="text-gray-600">Provider:</span>
                                <div className="font-medium">{batchData.provider || 'N/A'}</div>
                            </div>
                            <div>
                                <span className="text-gray-600">Total de Documentos:</span>
                                <div className="font-medium">{batchData.reports?.length || 0}</div>
                            </div>
                            <div>
                                <span className="text-gray-600">Status:</span>
                                <div className="font-medium">{batchData.status || 'N/A'}</div>
                            </div>
                            <div>
                                <span className="text-gray-600">Processado em:</span>
                                <div className="font-medium">
                                    {batchData.createdAt || 'N/A'}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <ATable headers={headers.value}>
                {rows.value.map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                        {headers.value.map((header) => (
                            <td key={header.id} className="px-4 py-2 border-b">
                                {row[header.id]}
                                </td>
                            ))}
                        </tr>
                    ))}
            </ATable>
        </PageForm>
    );
};

export default RecommendationEngineRecommendationView;
